import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import translate from '../../../commonComponents/translate';
import withCSRF from '../../../commonComponents/withCSRF';
import Anchor from '../../../commonComponents/Anchor';
import ShopPriceEstimation from '../shopItems/ShopPriceEstimation';
import CartStateProp from '../propTypes/cartStateProp';
import UserCurrencyProp from '../../../commonComponents/propTypes/userCurrencyProp';
import StripeCardProp from '../../../commonComponents/propTypes/stripeCardProp';
import {
  compose,
  FREQUENCIES_TYPES,
  PAYMENT_METHOD,
} from '../../../services/utilityFunctions';
import TenantProp, {
  PAYMENT_PROVIDER,
} from '../../../commonComponents/propTypes/tenantProp';
import orderPriceProp from '../propTypes/orderPriceProp';
import CheckoutTerms from '../components/CheckoutTerms';
import PaymentMethods from '../../../commonComponents/modal/components/PaymentMethods';
import Loader from '../../../services/loader';
import VatTypesProp from '../../../commonComponents/forms/Address/VatTypesProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import AddressProp from '../../../commonComponents/propTypes/addressProp';
import Notifications from '../../../services/notifications';
import ShopAvailableCoupon from '../ShopAvailableCoupon';
import { getProductsForOrder } from '../shopHelper';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import MarketInfosContext from '../../../contextProviders/MarketInfosContext';

function ShopProductPayment({
  t,
  cartState,
  userCurrency,
  creditCards,
  selectCreditCard,
  createOrder,
  tenant,
  goBack,
  onChangeNotes,
  orderDetails,
  isPaidWithCoins,
  orderAlreadyCreated,
  vatTypes,
  countries,
  handleCompanyBilling,
  updateCreditCards,
  directSellingActive,
  tabActive,
  companyBilling,
  onHandlePaymentTabs,
  isSubscription,
  updateCartCoupon,
  updateCoins,
  _csrf,
}) {
  const { paymentProviderType } = tenant?.settings || {};
  // External-payment-link product (BRAVON-6316): paid on an external Stripe
  // checkout — no internal payment method, no coupons/coins (the link charges
  // a fixed price on the external account). The order-calculate response is
  // the authoritative signal (server-derived from the product); the cart-item
  // flag only exists on carts built through Buy Now after this feature.
  const isExternalPayment =
    !!orderDetails?.externalPayment?.link ||
    cartState?.products?.some(p => p.externalPaymentLink);
  const { loggedUser } = useContext(LoggedUserContext);
  const { marketInfos } = useContext(MarketInfosContext);
  const userCountry =
    cartState?.billingAddress?.countryCode ||
    loggedUser?.address?.countryCode ||
    marketInfos.localCountry;

  const [coupons, setCoupons] = useState(null);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [selectedCoins, setSelectedCoins] = useState(null);
  const [textCoupon, setTextCoupon] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const handleLoading = loading => {
    setIsLoading(loading);
  };
  const loader = useRef(new Loader('#shopCheckoutPayment'));
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const handleAvailableCouponChange = data => {
    setCoupons(data);
  };

  const handleCouponClick = coupon => {
    // Make sure that we unselect the coupon if code is equal to the selected coupon
    if (
      coupon &&
      coupon.code === selectedCoupon &&
      cartState?.coupon?.code === coupon.code
    ) {
      setSelectedCoupon(null);
      updateCartCoupon(null);
    } else {
      setSelectedCoupon(coupon.code);
      updateCartCoupon(coupon.code);
    }
    setTextCoupon('');
  };

  const handleCoinsClick = coinsCoupon => {
    if (coinsCoupon && coinsCoupon.code === selectedCoins && isPaidWithCoins) {
      setSelectedCoins(null);
      updateCoins(false);
    } else {
      setSelectedCoins(coinsCoupon.code);
      updateCoins(true);
    }
  };

  const handleCouponCheck = () => {
    setSelectedCoupon('');
    updateCartCoupon(textCoupon);
  };

  const handleClearCoupon = () => {
    setSelectedCoupon(null);
    updateCartCoupon(null);
    setTextCoupon('');
  };

  const handleClearCoins = () => {
    setSelectedCoins(null);
    updateCoins(false);
  };

  useEffect(() => {
    if (cartState.products.length > 0 && loggedUser) {
      let availableCouponsReqPath =
        '/api/discount/coupon/available?moduleType=marketPlace';
      if (userCurrency) {
        availableCouponsReqPath += `&currency=${userCurrency.code}`;
      }
      if (userCountry) {
        availableCouponsReqPath += `&country=${userCountry}`;
      }
      axios
        .post(
          availableCouponsReqPath,
          {
            marketProductVariations: getProductsForOrder(
              cartState?.products,
              true,
            ),
          },
          {
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
              'X-CSRF-Token': _csrf,
              _csrf,
            },
          },
        )
        .then(({ data: response }) => {
          handleAvailableCouponChange(response.data);
        })
        .catch(err => {
          Notifications.showErrorFromRequest(err, 'error_getAvailableCoupons');
          console.log(err);
        });
    }
  }, [cartState.products]);

  /** Handling the different kind of payments */

  // Terms of conditions
  const [checkedTerms, setCheckedTerms] = useState(false);
  const handleCheckbox = () => {
    setCheckedTerms(!checkedTerms);
  };

  // HANDLE THE ACCOUNT HOLDER NAME FOR BANCONTACT
  const [accountHolderName, setAccountHolderName] = useState(null);

  // HANDLE THE 3 TYPES OF PAYMENT
  const disablePurchaseButton = () => {
    // External payment link: only terms acceptance is needed — the payment
    // happens on the external Stripe checkout after the redirect.
    if (isExternalPayment) {
      return !checkedTerms || cartState.isLoading;
    }
    // when payment provider is manual or none, subscriptions should not work at the end of the checkout
    if (
      isSubscription &&
      isSubscription !== 'only_once' &&
      paymentProviderType !== PAYMENT_PROVIDER.STRIPE &&
      paymentProviderType !== PAYMENT_PROVIDER.STRIPE_INVOICES
    ) {
      return true;
    }
    // If order total is 0 card is not needed
    if (orderDetails.total === 0) {
      return !checkedTerms || cartState.isLoading;
    }
    if (
      orderDetails.total > 0 &&
      paymentProviderType === PAYMENT_PROVIDER.NONE
    ) {
      return true;
    }
    if (tabActive === PAYMENT_METHOD.CREDIT_CARD) {
      return (
        !cartState.selectedCreditCard || !checkedTerms || cartState.isLoading
      );
    }
    if (
      tabActive === PAYMENT_METHOD.BANK_TRANSFER ||
      tabActive === PAYMENT_METHOD.CRYPTO_MANUAL ||
      tabActive === PAYMENT_METHOD.CHEQUE ||
      tabActive === PAYMENT_METHOD.PAYPAL ||
      tabActive === PAYMENT_METHOD.KLARNA
    ) {
      return !checkedTerms || cartState.isLoading;
    }
    if (tabActive === PAYMENT_METHOD.PAID_BY_COMPANY) {
      return !cartState.companyBilling || !checkedTerms || cartState.isLoading;
    }
    return (
      !checkedTerms ||
      !accountHolderName ||
      accountHolderName.length === 0 ||
      cartState.isLoading
    );
  };

  const handleCreateOrder = () => {
    // External payment link: create the order and let the checkout flow
    // redirect to the external Stripe page — no card/subscription checks.
    if (isExternalPayment) {
      createOrder(PAYMENT_METHOD.EXTERNAL_LINK);
      return;
    }
    // is subscription there is a value in subscription and no card
    const isSubscriptionNoCard =
      isSubscription &&
      tabActive !== PAYMENT_METHOD.PAYPAL && // ignore if is paypal
      !cartState.selectedCreditCard &&
      orderDetails?.subtotal >= 0;
    // is subscription there is a discount (coins or coupons) and there is a value to that
    const isSubscriptionAndDiscount =
      isSubscription &&
      orderDetails?.subtotal > 0 &&
      orderDetails?.coupon?.code;

    // if one of the cases happen, show error
    if (isSubscriptionNoCard || isSubscriptionAndDiscount) {
      Notifications.showNotificationError(
        t('error'),
        t('ERROR_NO_CARD_SUBSCRIPTION'),
      );
      return;
    }

    if (orderDetails.total > 0 || isSubscription) {
      createOrder(tabActive, accountHolderName);
    } else {
      createOrder(PAYMENT_METHOD.BANK_TRANSFER);
    }
  };

  const handlePaymentMethod = (type, payload) => {
    switch (type) {
      case 'updateHolderName':
        setAccountHolderName(payload.accountHolderName);
        break;
      case 'updatedCardSelected':
        selectCreditCard(payload.cardId);
        break;
      case 'updatedUserCards':
        updateCreditCards(payload.cards);
        selectCreditCard(payload.cardId);
        break;
      case 'removeCreditCard':
        updateCreditCards(payload.cards);
        break;
      case 'companyBilling':
        handleCompanyBilling(payload);
        break;
      case 'changeTab':
        onHandlePaymentTabs(payload.tab);
        break;
      default:
        console.log('*Default', type, payload);
    }
  };
  const handleRemoveCard = () => {
    selectCreditCard(null);
  };

  // We should always select a credit card for product subscriptions even if order.total === 0
  const showPaymentMethods =
    !isExternalPayment && (orderDetails.total > 0 || isSubscription);

  return (
    <div
      id="shopCheckoutPayment"
      className="m-portlet--rounded m-portlet--rounded-force m--margin-top-10 m--margin-bottom-10 shop-checkout-step3"
    >
      <div className="m-portlet__body">
        <div className="row no-padding">
          <div className="col-lg-8 col-md-8">
            <div
              style={{
                marginBottom: '2rem',
                display: isExternalPayment ? 'none' : undefined,
              }}
              className="m-portlet__body"
            >
              <div className="coupons-choose">
                <div style={{ marginBottom: '2rem' }}>
                  <h4>{t('shop.cart.coupon')}</h4>
                  <div className="form-group">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t('shop.cart.check_coupon_placeholder')}
                        value={textCoupon}
                        onChange={e => {
                          setTextCoupon(e.target.value);
                        }}
                        disabled={orderAlreadyCreated}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-theme btn-dynamic"
                          type="button"
                          disabled={
                            textCoupon.length === 0 || orderAlreadyCreated
                          }
                          onClick={handleCouponCheck}
                        >
                          {t('shop.cart.check')}
                        </button>
                      </div>
                    </div>
                    <span className="m-form__help">
                      {t('shop.cart.check_coupon_hint')}
                    </span>
                  </div>
                </div>

                <div className="title">
                  <h3>{t('shop.cart.apply_discount')}</h3>
                  <p>{t('shop.cart.choose_coupon_reward')}</p>
                </div>
                <div className="container">
                  <div className="row">
                    {coupons &&
                      coupons.map(coupon => (
                        <ShopAvailableCoupon
                          key={coupon._id}
                          coupon={coupon}
                          onClick={handleCouponClick}
                          selectedCoupon={selectedCoupon}
                          orderAlreadyCreated={orderAlreadyCreated}
                          selectedCoins={selectedCoins}
                          handleCoinsClick={handleCoinsClick}
                        />
                      ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="payment-type" />
            <div className="payment-manage line-list">
              <div className="m-portlet__body">
                {isExternalPayment && (
                  <>
                    <h3>{t('shop.checkout.payment.external')}</h3>
                    <p>{t('shop.checkout.payment.external.hint')}</p>
                    <hr />
                  </>
                )}
                {showPaymentMethods && (
                  <PaymentMethods
                    t={t}
                    cards={creditCards}
                    updatePaymentMethod={handlePaymentMethod}
                    tabActive={tabActive}
                    accountHolderName={accountHolderName}
                    selectedCreditCard={cartState.selectedCreditCard}
                    handleIsLoading={handleLoading}
                    tenant={tenant}
                    vatTypes={vatTypes}
                    countries={countries}
                    companyBilling={companyBilling}
                    isSubscription={isSubscription}
                    onRemoveCard={handleRemoveCard}
                    order={{ ...cartState, currency: userCurrency?.code }}
                    orderTotal={orderDetails.total}
                  />
                )}
                {!isExternalPayment && !showPaymentMethods && (
                  <>
                    <h3>{t('shop.checkout.payment.notRequired')}</h3>
                    <hr />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-4 continue-colum">
            <ShopPriceEstimation
              t={t}
              userCurrency={userCurrency}
              cartState={cartState}
              orderDetails={orderDetails}
              isPaidWithCoins={isPaidWithCoins}
              directSellingActive={directSellingActive}
              onClearCoupon={handleClearCoupon}
              onClearCoins={handleClearCoins}
              showClearCoupon
              showClearCoins
            />
            <div className="notes-card">
              <h3>{t('shop.cart.notes')}</h3>
              <textarea
                className="form-control notes-input"
                placeholder={t('shop.cart.notes.placeholder')}
                onChange={e => {
                  onChangeNotes(e.target.value);
                }}
                rows={5}
                disabled={orderAlreadyCreated}
              />
            </div>
            <CheckoutTerms
              onChange={handleCheckbox}
              checked={checkedTerms}
              tenant={tenant}
            />
            <Anchor
              onClick={handleCreateOrder}
              className={`landing-certification-browse btn m-btn--pill btn-dynamic loader-button${
                disablePurchaseButton() ? ' disabled' : ''
              }${
                cartState.isLoading
                  ? ' m-loader m-loader--right m-loader--light'
                  : ''
              }`}
            >
              {t('shop.cart.complete_order')}
            </Anchor>
            <Anchor
              className="btn m-btn--pill btn-metal shop-btnBack"
              onClick={() => {
                goBack('payment');
              }}
            >
              {t('back')}
            </Anchor>
          </div>
        </div>
      </div>
    </div>
  );
}

ShopProductPayment.propTypes = {
  t: PropTypes.func.isRequired,
  isPaidWithCoins: PropTypes.bool.isRequired,
  cartState: PropTypes.shape(CartStateProp.propType).isRequired,
  goBack: PropTypes.func.isRequired,
  onChangeNotes: PropTypes.func.isRequired,
  userCurrency: PropTypes.shape(UserCurrencyProp.propType).isRequired,
  creditCards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType))
    .isRequired,
  selectCreditCard: PropTypes.func.isRequired,
  createOrder: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propTypes),
  orderDetails: PropTypes.shape(orderPriceProp.propType),
  orderAlreadyCreated: PropTypes.string,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  handleCompanyBilling: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  updateCreditCards: PropTypes.func.isRequired,
  onHandlePaymentTabs: PropTypes.func.isRequired,
  directSellingActive: PropTypes.bool.isRequired,
  tabActive: PropTypes.string.isRequired,
  companyBilling: PropTypes.shape({
    address: PropTypes.shape(AddressProp.propType),
    name: PropTypes.string,
    phone: PropTypes.string,
    taxCode: PropTypes.string,
    vatType: PropTypes.string,
    vatNumber: PropTypes.string,
  }),
  isSubscription: PropTypes.oneOf(FREQUENCIES_TYPES),
  _csrf: PropTypes.string.isRequired,
  updateCartCoupon: PropTypes.func.isRequired,
  updateCoins: PropTypes.func.isRequired,
};

ShopProductPayment.defaultProps = {
  tenant: {},
  orderDetails: PropTypes.shape(orderPriceProp.defaultProp),
  orderAlreadyCreated: null,
  companyBilling: {},
  isSubscription: null,
};

export default compose(translate, withCSRF)(ShopProductPayment);
